import { useMemo } from 'react'
import { MenuItem, Stack, Grid, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import 'react-phone-input-2/lib/high-res.css'
import { useUser } from 'store'
import TextField from 'packages/ui/TextField'
import TabPanel from 'components/TabPanel'
import ActionButtons from './ActionButtons'
import { CIVILITIES, COUNTRY, LANGUAGES, LANGUAGE_TO_COUNTRY } from '../constants'
import { toast } from 'components/global/CUK/Toast/Toast'
import { useSitesSelected, useTreeContext } from 'packages/ui/Arborist/TreeProvider'
import Switch from 'packages/ui/Switch'
import Typography from '@mui/material/Typography'

const Information = ({ onSubmit, loading, isLocked }) => {
  const {
    control,
    watch,
    trigger,
    setValue,
    formState: { errors }
  } = useFormContext()

  const { t } = useTranslation()
  const sfId = watch('sf_id')
  const twoFaActivated = watch('two_fa_activated')
  const internal = watch('internal')
  const [user] = useUser()

  const disabled = useMemo(() => {
    if (!user) {
      return true
    } else if (!(user.roles.admin_contact || user.admin)) {
      return true
    }
    return false
  }, [user])

  return (
    <TabPanel>
      <Stack sx={{ 
        height: 'calc(100vh - 215px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box flex={1} overflow="auto">
          <Grid container spacing={2}>
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                name={'salutation'}
                control={control}
                select
                error={errors.salutation?.message}
                label={t('auth.admin.edit.title_placeholder')}
                sx={{
                  width: '120px'
                }}>
                {CIVILITIES.map(option => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                name={'first_name'}
                control={control}
                error={errors.first_name?.message}
                variant="outlined"
                label={t('auth.admin.edit.firstname_placeholder')}
              />
            </Grid>
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                name={'last_name'}
                control={control}
                error={errors.last_name?.message}
                variant="outlined"
                label={t('auth.admin.edit.lastname_placeholder')}
              />
            </Grid>
            <Grid xs={3} item />
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                name={'email'}
                control={control}
                disabled={true}
                variant="outlined"
                label={t('auth.admin.edit.email_placeholder')}
              />
            </Grid>
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                name={'title'}
                control={control}
                error={errors?.title?.message ? true : false}
                variant="outlined"
                label={t('auth.admin.edit.position_placeholder')}
              />
            </Grid>
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                name={'langue'}
                control={control}
                error={errors?.langue?.message ? true : false}
                select
                label={'Langue privilégié'}
                onChange={(selectedValue) => {
                  setValue('langue', selectedValue)
                  setValue('country', LANGUAGE_TO_COUNTRY[selectedValue])
                }}
              >
                {LANGUAGES.map(option => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid xs={3} item />
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                name={'country'}
                control={control}
                error={errors?.country?.message ? true : false}
                select
                label={'Pays'}>
                {COUNTRY.map(option => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                variant="outlined"
                name={'mobile_phone'}
                error={errors.mobile_phone}
                onChange={() => trigger('phones')}
                control={control}
                type="phone"
                label={t('auth.admin.edit.mobile_placeholder')}
              />
            </Grid>
            <Grid xs={3} item>
              <TextField
                disabled={disabled}
                variant="outlined"
                name={'phone'}
                error={errors.phone}
                control={control}
                type="phone"
                label={t('auth.admin.edit.phone_placeholder')}
              />
            </Grid>
            <Grid xs={3} item />
            {!internal && (
              <Grid xs={12} item>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-start'} spacing={1}>
                  <Switch
                    checked={twoFaActivated}
                    disabled={!user.admin}
                    onClick={() => {
                      setValue('two_fa_activated', !twoFaActivated)
                    }}
                  />
                  <Typography variant="caption" sx={{ textAlign: 'left' }}>
                    <Box component="span" sx={{ color: 'black' }}>{t('two_fa_disclaimer.title')}</Box>
                  </Typography>
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #eee'
        }}>
          <ActionButtons
            loading={loading}
            onSubmit={onSubmit}
            disabled={disabled || isLocked}
            labelSubmit={sfId ? t('ui_elements.form_validate') : t('ui_elements.form_next')}
          />
        </Box>
      </Stack>
    </TabPanel>
  )  
}

const Form = ({ onSubmit, loading, isLocked, isInternal }) => {
  const { t } = useTranslation()
  const { sfId } = useParams()
  const { formState: { errors } } = useFormContext()
  const { selection } = useTreeContext()
  const { count: nbSitesSelected } = useSitesSelected({ selection })

  const handleSubmit = async () => {
    if (Object.entries(errors).length > 0) {
      toast.error(t('auth.admin.edit.form_errors'));
      return;
    }

    if (!sfId.includes('@') && nbSitesSelected === 0 && !isInternal) {
      toast.error(t('auth.admin.edit.sites_selected_errors'));
      return;
    }

    onSubmit();
  };

  return <Information onSubmit={handleSubmit} loading={loading} isLocked={isLocked} isInternal={isInternal} />
}

export default Form